import { Component, OnInit, ViewChild, ContentChild, ElementRef } from '@angular/core';
import { Select2Option } from '../../../../shared/select2-option';
import { CustomerService } from '../../../../ect-feature/services/customer.service';
import { Alert } from '../../../../shared/alert.model';
import { getAlertMessage } from '../../../../shared/api-loaders';
import { Router, ActivatedRoute } from '@angular/router';
import { ModalService } from '../../../../shared/modal.service';
import { ProfileSyncConfirmationDialogComponent } from '../profile-sync-confirmation-dialog/profile-sync-confirmation-dialog.component';
import { NgForm } from '@angular/forms';
import { ProfileSyncService } from '../../../services/profile-sync.service';
import { ProfileSyncData } from '../../../model/ProfileSyncData';
import { ParameterService } from "../../../../ect-feature/services/parameter.service";
import { MessageService } from '../../../../shared/message.service';
import { MessageType } from '../../../../shared/messageType';

@Component({
    selector: 're-profile-sync-run',
    templateUrl: './profile-sync-run.component.html',
    styleUrls: ['./profile-sync-run.component.scss'],
    standalone: false
})
export class ProfileSyncRunComponent implements OnInit {

  constructor(private customerService: CustomerService,
    private router: Router,
    private route: ActivatedRoute,
    private modalService: ModalService,
    private profileSyncService: ProfileSyncService,
    private parameterService: ParameterService,
    private messageService: MessageService
  ) { }

  customerOptions: Array<Select2Option>;
  environmentOptions: Array<Select2Option>;
  corporationOptions: Array<Select2Option>;

  syncData = new ProfileSyncData();

  customerName: string;
  environmentName: string;
  corporationName: string;
  alert: Alert;
  isPhysicianFeatureEnabled = false;
  physicianNppaLabel = 'Sync Physician/NPPA Profiles';
  referenceData: any[]; // Corporate structure data

  ngOnInit() {
    this.loadCustomers();
  }

  private showSpinner() {
    this.messageService.send(MessageType.ProcessingOn);
  }

  private hideSpinner() {
    this.messageService.send(MessageType.ProcessingOff);
  }

  loadCustomers() {
    this.showSpinner();
    this.customerService.getCustomersByStack().subscribe(
      x => {
        this.customerOptions = x.map<Select2Option>(c => ({ data: c.accountId, id: c.id.toString(), text: c.accountName }));
        this.hideSpinner();
      },
      err => {
        this.hideSpinner();
        this.alert = getAlertMessage(err);
      }
    );
  }
  @ViewChild("NgForm")
  form: NgForm;

  customerId: number;

  customerChanged(e: Select2Option) {
    this.customerName = e.text;
    var selectedCustomer = this.customerOptions.firstOrDefault(x => +x.id == this.customerId);
    this.syncData.crmId = selectedCustomer.data;

    this.showSpinner();
    this.customerService.getCorporateStructureByCrmId(this.syncData.crmId).subscribe(
      (data) => {
        this.referenceData = data;
        this.loadEnvironments();
        this.hideSpinner();
      },
      (err) => {
        this.hideSpinner();
        this.alert = getAlertMessage(err);
      }
    );
  }
  environmentChanged(e: Select2Option) {
    this.environmentName = e.text;
    this.loadCorporations();
    this.loadPhysicianFeatureFlag();
  }
  corporationChanged(e: Select2Option) {
    this.corporationName = e.text;
    let selectedCoporation = this.corporationOptions.firstOrDefault(x => +x.id == this.syncData.corpId);
    this.syncData.environmentId = selectedCoporation.data
  }

  loadEnvironments() {
    if (!this.referenceData) {
      this.environmentOptions = [];
      return;
    }

    // Extract unique environments from corporate structure data
    const uniqueEnvironments = this.referenceData
      .map<Select2Option>((e) => ({
        id: e.configId.toString(),
        text: e.configName,
      }))
      .filter((env, index, self) =>
        index === self.findIndex((t) => t.id === env.id)
      );

    this.environmentOptions = [
      { id: "", text: 'Not Selected' },
      ...uniqueEnvironments
    ];

    this.syncData.configId = null;
    this.syncData.corpId = null;
    this.corporationOptions = [];
  }
  loadCorporations() {
    this.syncData.corpId = null;
    this.profileSyncService.getCorporations(this.syncData.configId).subscribe(x => {
      const corporations = x.map<Select2Option>(e => ({ data: e.environmentID, id: e.corporationID.toString(), text: e.corpName }));

      this.corporationOptions = [
        { id: "", text: 'Not Selected', data: null },
        ...corporations
      ];

    }, err => this.alert = getAlertMessage(err))
  }


  loadPhysicianFeatureFlag() {
    if (!this.syncData.configId) {
      this.isPhysicianFeatureEnabled = false;
      return;
    }

    // Use getParametersByPartialKey to get the physician feature flag for this config
    this.parameterService.getParametersByPartialKey(this.syncData.configId.toString(), 'bp.idm.auth.physician.enabled', true).subscribe(
      parameters => {
        this.isPhysicianFeatureEnabled = false;
        if (parameters && parameters.length > 0) {
          const physicianParam = parameters.find(p => p.key?.toLowerCase() === 'bp.idm.auth.physician.enabled');
          if (physicianParam.value.toLowerCase()=== 'true') {
            this.isPhysicianFeatureEnabled = physicianParam.value?.toLowerCase() === 'true';
          }
        }
      },
      err => {
        this.alert = getAlertMessage(err);
        this.isPhysicianFeatureEnabled = false;
      }
    );
  }


  back() {
    this.router.navigate([".."], { relativeTo: this.route })
  }

  running = false;

  async syncPhysicianNppa() {
    let c = this.modalService.create<ProfileSyncConfirmationDialogComponent>(ProfileSyncConfirmationDialogComponent);
    c.component.customerName = this.customerName;
    c.component.environmentName = this.environmentName;
    c.component.corporationName = this.corporationName;
    c.component.dialogTitle = this.physicianNppaLabel;
    c.component.confirmButtonText = `Yes, ${this.physicianNppaLabel}`;
    let result = await c.modalRef.result;
    if (result) {
      this.running = true;
      let syncData: ProfileSyncData = {
        ...this.syncData,
        corpId: +this.syncData.corpId,
        environmentId: +this.syncData.environmentId,
        configId: +this.syncData.configId
      };
      this.profileSyncService.syncPhysicianNppa(syncData).subscribe(x => {
        this.back();
      }, err => {
        this.alert = getAlertMessage(err);
        this.running = false;
      }, () => {
        this.running = false;
      });
    }
  }

  async sync() {
    let c = this.modalService.create<ProfileSyncConfirmationDialogComponent>(ProfileSyncConfirmationDialogComponent);
    c.component.customerName = this.customerName;
    c.component.environmentName = this.environmentName;
    c.component.corporationName = this.corporationName;
    let result = await c.modalRef.result;
    if (result) {
      this.running = true;
      // select2 selected values are always strings
      // lets convert those values to numbers on the front end
      let syncData: ProfileSyncData = {
        ...this.syncData,
        corpId: +this.syncData.corpId,
        environmentId: +this.syncData.environmentId,
        configId: +this.syncData.configId
      };

      //only include if feature is enabled
      if(this.isPhysicianFeatureEnabled){
        syncData.includePhysicianNppaUsers=true;
      }

      this.profileSyncService.sync(syncData).subscribe(x => {

        this.back();
      }, err => {
        this.alert = getAlertMessage(err);
        this.running = false;
      }, () => {
        this.running = false;
      })
    }
  }
}
